import { useState } from 'react';
import {
  ActionIcon, Indicator, Popover, Text, Group, ScrollArea, Stack, Box,
  ThemeIcon, UnstyledButton, Button, Center, Loader, Tooltip,
} from '@mantine/core';
import { IconBell, IconChecks } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';
import { NOTIF_TYPE_META, NotifType } from '../lib/notifTypes';
import { timeAgo } from '../lib/format';

interface NotifData {
  screen?: string; url?: string; studentId?: string; classId?: string; userId?: string;
  /** Shartnoma bildirishnomasi (`kind: 'contract'`) — qaysi shartnoma haqida. */
  contractId?: string;
}
interface Notif {
  id: string;
  type: NotifType;
  title: string;
  body: string;
  data?: NotifData | null;
  readAt: string | null;
  createdAt: string;
}

// Bildirishnoma data.screen → admin panel yo'li. studentId bo'lsa — o'quvchi sahifasi.
// 'Profile'/'Premium' (ilova ota-onalari uchun) admin panelda sahifaga ega emas — xaritada
// yo'q, shuning uchun bosilganda faqat o'qilgan deb belgilanadi (avval mavjud bo'lmagan
// /premium ga yuborib, dashboard'ga chalg'ituvchi qaytarardi).
const SCREEN_ROUTE: Record<string, string> = {
  Support: '/support', Announcements: '/announcements', Attendance: '/attendance',
  Grades: '/grades', Homework: '/homework', Payments: '/payments',
  Admissions: '/admissions', // saytdan kelgan qabul arizasi (backend routes/public.ts)
  AccountDeletion: '/settings#akkaunt-ochirish', // akkauntni o'chirish so'rovi (backend lib/akkauntOchirish.ts)
};

/**
 * Shartnoma bildirishnomasining ekran nomlari (backend `lib/contractSign.ts`):
 * xodimga `screen: 'Contract'`, ota-onaga (mobil ilova) `screen: 'Contracts'` ketadi —
 * admin panelda ikkalasi ham SHARTNOMALAR ro'yxatiga olib boradi. Avval bu ekranlar
 * xaritada yo'q edi va bildirishnoma bosilganda HECH NIMA bo'lmasdi.
 */
const CONTRACT_SCREENS = ['Contract', 'Contracts'];

/** Header qo'ng'irog'i — o'z bildirishnomalari: badge, ro'yxat, o'qilgan deb belgilash. */
export function HeaderBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opened, setOpened] = useState(false);

  const { data: unread } = useQuery({
    queryKey: ['my-unread-count'],
    queryFn: async () => (await api.get<{ count: number }>('/notifications/unread-count')).data.count,
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  const { data: items, isLoading } = useQuery({
    queryKey: ['my-notifications'],
    queryFn: async () => (await api.get<Notif[]>('/notifications')).data,
    enabled: opened, // faqat popover ochilganda yuklanadi
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['my-notifications'] });
    qc.invalidateQueries({ queryKey: ['my-unread-count'] });
  };

  const readOne = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: invalidate,
  });
  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: invalidate,
  });

  const canSeeAll = canAccess(user?.role, 'notifications');

  // Bildirishnoma bosilganda: o'qilgan deb belgilaydi + tegishli sahifaga o'tadi.
  const onOpenNotif = (n: Notif) => {
    if (!n.readAt) readOne.mutate(n.id);
    const d = n.data ?? {};
    // Tashqi havola — yangi tabda
    if (d.url && /^https?:\/\//.test(d.url)) { window.open(d.url, '_blank', 'noopener,noreferrer'); return; }
    const screen = d.screen ?? '';
    // Shartnoma — alohida bo'lim (`contracts` ruxsati). Ruxsati yo'q rolni (masalan
    // tenant ichidagi SUPER_ADMIN) Guard'ning "Ruxsat yo'q" ekraniga otib yubormaymiz.
    if (CONTRACT_SCREENS.includes(screen)) {
      if (!canAccess(user?.role, 'contracts')) {
        // JIM turmaymiz: bosildi-yu hech narsa bo'lmasa, foydalanuvchi buni nosozlik deb
        // o'ylaydi. Guard ekraniga otib yubormasdan sababini aytamiz.
        notifications.show({ color: 'gray', message: t('guard.deniedMsg') });
        return;
      }
      setOpened(false);
      // `id` — ro'yxat sahifasi kelajakda AYNAN shu shartnoma kartochkasini ocha olishi uchun
      navigate(d.contractId ? `/contracts?id=${encodeURIComponent(d.contractId)}` : '/contracts');
      return;
    }
    // Bitta o'quvchiga tegishli (baho/to'lov/davomat) — o'quvchi sahifasiga
    const path = d.studentId && ['Grades', 'Payments', 'Attendance'].includes(screen)
      ? `/students/${d.studentId}`
      : SCREEN_ROUTE[screen];
    if (path) { setOpened(false); navigate(path); }
  };

  return (
    <Popover width={380} position="bottom-end" shadow="lg" opened={opened} onChange={setOpened}>
      <Popover.Target>
        <Tooltip label={t('nav.notifications')}>
          <Indicator
            color="red"
            size={16}
            offset={4}
            disabled={!unread}
            label={unread && unread > 99 ? '99+' : unread || undefined}
          >
            <ActionIcon variant="default" size="lg" aria-label="notifications" onClick={() => setOpened((v) => !v)}>
              <IconBell size={18} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="md" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={700} size="sm">{t('nav.notifications')}</Text>
          {(unread ?? 0) > 0 && (
            <Button
              variant="subtle" size="compact-xs" leftSection={<IconChecks size={14} />}
              onClick={() => readAll.mutate()} loading={readAll.isPending}
            >
              {t('notif.markAll')}
            </Button>
          )}
        </Group>

        <ScrollArea.Autosize mah={380}>
          {isLoading ? (
            <Center py="lg"><Loader size="sm" /></Center>
          ) : !items?.length ? (
            <Center py="lg"><Text size="sm" c="dimmed">{t('pages.notifications.empty')}</Text></Center>
          ) : (
            <Stack gap={0}>
              {items.map((n) => {
                const meta = NOTIF_TYPE_META[n.type] ?? NOTIF_TYPE_META.SYSTEM;
                const Ico = meta.icon;
                const isUnread = !n.readAt;
                return (
                  <UnstyledButton
                    key={n.id}
                    onClick={() => onOpenNotif(n)}
                    px="md" py="sm"
                    className="sata-hoverable"
                    style={{
                      borderBottom: '1px solid var(--mantine-color-default-border)',
                      background: isUnread ? 'var(--mantine-color-brand-light)' : undefined,
                    }}
                  >
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon variant="light" color={meta.color} size={34} radius="md">
                        <Ico size={17} />
                      </ThemeIcon>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={isUnread ? 700 : 600} lineClamp={1}>{n.title}</Text>
                        <Text size="xs" c="dimmed" lineClamp={2}>{n.body}</Text>
                        <Text size="xs" c="dimmed" mt={2}>{timeAgo(n.createdAt)}</Text>
                      </Box>
                      {isUnread && (
                        <Box w={8} h={8} mt={6} style={{ borderRadius: 4, background: 'var(--mantine-color-red-6)', flexShrink: 0 }} />
                      )}
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>
          )}
        </ScrollArea.Autosize>

        {canSeeAll && (
          <Box p="xs" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <Button
              fullWidth variant="light" size="xs"
              onClick={() => { setOpened(false); navigate('/notifications'); }}
            >
              {t('notif.viewAll')}
            </Button>
          </Box>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
